/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { CatalogClientExtension } from '@agilelab/plugin-wb-catalog-extension-common';
import { CodeSnippet, WbTable } from '@agilelab/plugin-wb-platform';
import { DEFAULT_NAMESPACE, parseEntityRef } from '@backstage/catalog-model';
import {
  TableColumn,
  TableProps,
  WarningPanel,
} from '@backstage/core-components';
import {
  identityApiRef,
  useApi,
  useRouteRef,
} from '@backstage/core-plugin-api';
import { entityRouteRef, useEntityList } from '@backstage/plugin-catalog-react';
import _ from 'lodash';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { usePrevious } from 'react-use';
import useAsync from 'react-use/lib/useAsync';
import { extensionApiRef } from '../../CatalogExtensionApiRef';
import { CatalogTableFilters } from './CatalogTableFilters/CatalogTableFilters';
import { columnFactories } from './columns';
import { useColumns } from './hooks/useColumns';
import { useRows } from './hooks/useRows';
import { CatalogTableRow } from './types';
import { useDomainsEntities } from '../../hooks';
import { useQuery } from '@tanstack/react-query';
import { extractPartOfSystemRelations } from './utils';
import { WitboostSystem } from '@agilelab/plugin-wb-builder-common';

type EntityKind =
  | 'system'
  | 'component'
  | 'user'
  | 'group'
  | 'template'
  | 'edittemplate'
  | 'blueprint'
  | 'reverseprovisioningtemplate'
  | 'systemprototypetemplate'
  | 'accesscontrolrequesttemplate';

/**
 * Props for {@link CatalogTable}.
 *
 * @public
 */
export interface CatalogTableProps {
  columns?: TableColumn<CatalogTableRow>[];
  actions?: TableProps<CatalogTableRow>['actions'];
  tableOptions?: TableProps<CatalogTableRow>['options'];
  emptyContent?: ReactNode;
  subtitle?: string;
  isSoftwareCatalog?: boolean;
  filtersNode?: React.JSX.Element;
  columnKind?: EntityKind;
  actionsNode?: React.JSX.Element;
  preUrl?: string;
  availableResourceTypes?: Map<string, { displayName: string }>;
  limit: number;
}

/** @public */
export const CatalogTable = (props: CatalogTableProps) => {
  const {
    columns,
    actions,
    isSoftwareCatalog = false,
    filtersNode,
    actionsNode,
    columnKind,
    preUrl = '',
    availableResourceTypes,
    limit = 25,
  } = props;
  // TODO Check if backstage update allow to sort entities
  const { loading, error, entities, filters, pageInfo, totalItems } =
    useEntityList();
  const catalogApi: CatalogClientExtension = useApi(extensionApiRef);
  const identityApi = useApi(identityApiRef);
  const entityRoute = useRouteRef(entityRouteRef);
  const navigate = useNavigate();
  const previousFiltersState = usePrevious(filters);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!_.isEqual(previousFiltersState, filters)) {
      setPage(0);
    }
  }, [filters, previousFiltersState]);

  const entityKind = useMemo(() => {
    return columnKind ?? filters.kind?.value.toLowerCase();
  }, [columnKind, filters.kind?.value]);

  const KINDS_THAT_NEED_DOMAINS_QUERY: EntityKind[] = [
    'system',
    'template',
    'blueprint',
  ];

  // fetch domain entities only when current type needs it
  const { data: domains } = useDomainsEntities({
    enabled:
      entityKind !== undefined &&
      KINDS_THAT_NEED_DOMAINS_QUERY.includes(entityKind as EntityKind),
  });

  const systemRelationNamesToResolve = useMemo(() => {
    if (entityKind !== 'component') {
      return [];
    }

    const systemsSet: Set<string> = new Set();
    entities.forEach(entity => {
      const partOfSystemRelations = extractPartOfSystemRelations(entity);
      partOfSystemRelations.forEach(r => systemsSet.add(r.name));
    });

    return [...systemsSet];
  }, [entities, entityKind]);

  // fetch system names based on "partOf" relations of components (only when kind is component)
  const { data: systemsNameMap } = useQuery({
    queryKey: ['catalog-systems-names-map', systemRelationNamesToResolve],
    queryFn: async () => {
      const systemNameMap = new Map<string, string>();
      const systemsEntities = (
        await catalogApi.getEntities({
          filter: {
            kind: ['system'],
            ['metadata.name']: [...systemRelationNamesToResolve],
          },
          fields: ['metadata.name', 'spec.mesh.name'],
        })
      ).items as WitboostSystem[];

      systemsEntities.forEach(system => {
        // store names in lowercase to avoid issues when matching relations which could have different casing for the same name
        const name = system.metadata.name.toLowerCase();
        const meshName = system.spec?.mesh?.name;
        systemNameMap.set(name, meshName ?? system.metadata.name);
      });
      return systemNameMap;
    },
    enabled:
      entityKind === 'component' && systemRelationNamesToResolve.length > 0,
    staleTime: 5 * 60 * 1000, // Cache the result for 5 minutes
  });

  const { value: errors } = useAsync(async () => {
    const entityIds = entities.flatMap(e =>
      e.metadata.uid ? [e.metadata.uid] : [],
    );
    const { token } = await identityApi.getCredentials();
    const result = await catalogApi.getEntityErrors(entityIds, { token });
    return result ?? [];
  }, [catalogApi, entities]);

  const defaultColumns: TableColumn<CatalogTableRow>[] = useMemo(() => {
    return [...createEntitySpecificColumns()];
    function createEntitySpecificColumns(): TableColumn<CatalogTableRow>[] {
      switch (entityKind) {
        case 'user':
          return [
            columnFactories.createUserNameColumn(),
            columnFactories.createUserEmailColumn(),
            columnFactories.createGroupsColumn(),
          ];
        case 'domain':
          return [
            columnFactories.createNameColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createDomainTypeColumn({
              fieldPath: 'spec.instanceOf',
            }),
            columnFactories.createMetadataDescriptionColumn(),
            columnFactories.createOwnerColumn(),
            columnFactories.createTagsColumn(),
          ];
        case 'trait':
          return [
            columnFactories.createTraitNameColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createMetadataDescriptionColumn(),
          ];

        case 'system':
          return [
            columnFactories.createQualifiedNameColumn(),
            columnFactories.createVersionColumn(),
            columnFactories.createProjectTypeColumn(availableResourceTypes),
            columnFactories.createMetadataDescriptionColumn(),
            columnFactories.createDomainDataProductColumn(domains?.items || []),
            columnFactories.createOwnerColumn(),
          ];
        case 'location':
        case 'group':
          return [
            columnFactories.createNameColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createMetadataDescriptionColumn(),
            columnFactories.createSpecTypeColumn(),
          ];
        case 'template':
          return [
            columnFactories.createNameColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createMetadataDescriptionColumn(),
            columnFactories.createSpecTypeColumn(),
            columnFactories.createDomainDataProductColumn(domains?.items || []),
            columnFactories.createOwnerColumn(),
            columnFactories.createTagsColumn(),
          ];
        case 'edittemplate':
          return [
            columnFactories.createNameColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createMetadataDescriptionColumn(),
            columnFactories.createSpecTypeColumn(),
            columnFactories.createOwnerColumn(),
            columnFactories.createTagsColumn(),
          ];
        case 'component':
          return [
            columnFactories.createQualifiedNameColumn(),
            columnFactories.createMetadataDescriptionColumn(),
            columnFactories.createSpecTypeColumn(),
            columnFactories.createSystemColumn(),
            columnFactories.createVersionColumn(),
            columnFactories.createTagsColumn(),
          ];
        case 'blueprint':
          return [
            columnFactories.createNameColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createMetadataDescriptionColumn(),
            columnFactories.createDomainDataProductColumn(domains?.items || []),
            columnFactories.createOwnerColumn(),
            columnFactories.createTagsColumn(),
          ];
        case 'reverseprovisioningtemplate':
          return [
            columnFactories.createNameColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createMetadataDescriptionColumn(),
            columnFactories.createOwnerColumn(),
            columnFactories.createTagsColumn(),
          ];
        case 'release':
          return [
            columnFactories.createNameColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createReleaseSystemColumn(),
            columnFactories.createReleaseVersionColumn(),
            // creation date
            columnFactories.createCreationDateColumn(),
          ];
        case 'resource':
          return [
            columnFactories.createNameColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createMetadataDescriptionColumn(),
            columnFactories.createSpecTypeColumn(),
            columnFactories.createOwnerColumn(),
          ];
        case 'taxonomy':
          return [
            columnFactories.createNamePracticeShaperColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createMetadataDescriptionColumn(),
            columnFactories.createEnabledColumn(),
          ];
        case 'domaintype':
          return [
            columnFactories.createNamePracticeShaperColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createMetadataDescriptionColumn(),
          ];
        case 'systemtype':
          return [
            columnFactories.createNamePracticeShaperColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createMetadataDescriptionColumn(),
            columnFactories.createResourceIDColumn(),
            columnFactories.createTaxonomyColumn(),
            columnFactories.createDomainTypeColumn(),
          ];
        case 'componenttype':
          return [
            columnFactories.createNamePracticeShaperColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createMetadataDescriptionColumn(),
            columnFactories.createResourceIDColumn(),
            columnFactories.createSystemTypeColumn(),
          ];
        case 'systemprototypetemplate':
          return [
            columnFactories.createNameColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createSpecTypeColumn(),
            columnFactories.createMetadataDescriptionColumn(),
            columnFactories.createOwnerColumn(),
          ];
        case 'accesscontrolrequesttemplate':
          return [
            columnFactories.createNameColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createSpecTypeColumn(),
            columnFactories.createMetadataDescriptionColumn(),
            columnFactories.createOwnerColumn(),
          ];
        case 'systemprototype':
          return [
            columnFactories.createNameColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createSpecTypeColumn(),
            columnFactories.createMetadataDescriptionColumn(),
          ];
        case 'systemprototypetype':
          return [
            columnFactories.createNameColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createSpecTypeColumn(),
            columnFactories.createMetadataDescriptionColumn(),
          ];
        case 'taxonomytype':
          return [
            columnFactories.createNamePracticeShaperColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createMetadataDescriptionColumn(),
          ];

        default:
          return [
            columnFactories.createNameColumn({
              defaultKind: filters.kind?.value,
            }),
            columnFactories.createOwnerColumn(),
            columnFactories.createSpecTypeColumn(),
            columnFactories.createMetadataDescriptionColumn(),
          ];
      }
    }
  }, [entityKind, filters.kind?.value, domains, availableResourceTypes]);
  const filteredRows = useRows({
    errors,
    systemsNameMap: systemsNameMap,
    entities,
  });

  const cols = useColumns({
    columns: columns || defaultColumns,
    actions: actions,
  });

  if (error) {
    return (
      <div>
        <WarningPanel
          severity="error"
          title="Could not fetch Witboost entities."
        >
          <CodeSnippet language="text" text={error.toString()} />
        </WarningPanel>
      </div>
    );
  }
  return (
    <>
      <CatalogTableFilters
        isSoftwareCatalog={isSoftwareCatalog}
        filtersNode={filtersNode}
        actionsNode={actionsNode}
        kind={columnKind ?? filters.kind?.value.toLowerCase()}
      />

      <WbTable<CatalogTableRow>
        components={{
          tableLoader: { loading },
          tableContent: {
            columns: cols,
            rows: filteredRows,
            onRowClick: (e, ev) => {
              const tag = (ev.target as HTMLElement).tagName;
              if (tag !== 'A') {
                const data = {
                  name: e.entity.metadata.name ?? '',
                  kind: e.entity.kind,
                  namespace: e.entity.metadata.namespace ?? '',
                };

                data.kind = data.kind.toLocaleLowerCase('en-US');
                data.namespace =
                  data.namespace?.toLocaleLowerCase('en-US') ??
                  DEFAULT_NAMESPACE;
                const ref = parseEntityRef(data);
                navigate(`${preUrl}${entityRoute(ref)}`);
              }
            },
          },
        }}
        pagination={{
          rowsPerPageOptions: [],
          count: totalItems ?? 0,
          limit,
          offset: page * limit,
          onPageChange: (newPage: number) => {
            if (newPage > page) {
              pageInfo?.next?.();
            } else {
              pageInfo?.prev?.();
            }
            setPage(newPage);
          },
          onRowsPerPageChange: (_newRowsPerPage: number) => {
            // TODO (gabriele): update this part of the code by adding the call to "setLimit" hook when upgrading the Backstage version
            setPage(0);
          },
        }}
      />
    </>
  );
};

CatalogTable.columns = columnFactories;
